import * as React from 'react';
import { Link } from 'react-router-dom';
import { alpha, Box, Tooltip, Typography, useTheme } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { type LazyBreadcrumbsProps, TextIcon, Toolbar } from '@europrocurement/flexy-components';
import { FaOptionIcon, homeIcon, NamedIconsType, openIcon } from '@europrocurement/l2d-icons';

export type FicheSubtitleElement = {
    icon: IconProp;
    label?: string | number | null;
    /** Si fourni, ajoute une icône-lien cliquable après le label (ctrl+clic = nouvel onglet). */
    link?: string;
    /** Tooltip de l'icône-lien. */
    linkTooltip?: string;
};

const LINK_ICON = openIcon.props.icon;

export type FicheHeaderProps = {
    /** Icône du modèle affichée dans le badge. */
    icon: NamedIconsType;
    /** Titre principal (nom de l'élément, ou « Création … » en mode création). */
    title: string;
    /** Référence affichée en `#xxx` à côté du titre (généralement l'identifiant). */
    reference?: string | number | null;
    /** Éléments de la ligne de sous-titre (icône + label). Les labels vides sont ignorés. */
    subtitleElements?: FicheSubtitleElement[];
    /** Libellé du niveau intermédiaire du fil d'Ariane (section parente). */
    sectionLabel: string;
    /** Lien vers la liste de la section (fil d'Ariane + flèche retour). */
    listPath: string;
};

const HOME_ICON = homeIcon.props.icon;

/**
 * En-tête commun aux fiches de configuration (offres & comptabilité).
 *
 * Reprend le pattern de `PrescriberHeader` / `PrescriberDetail` : une `Toolbar`
 * (flèche retour + fil d'Ariane `Accueil / <section>`) suivie d'un bloc en-tête
 * (badge icône + titre + référence + ligne de sous-titre).
 */
const FicheHeader: React.FunctionComponent<FicheHeaderProps> = function ({
    icon,
    title,
    reference,
    subtitleElements = [],
    sectionLabel,
    listPath,
}) {
    const theme = useTheme();

    const breadcrumbPath: LazyBreadcrumbsProps['path'] = [
        {
            icon: { icon: HOME_ICON, sx: { alignItems: 'center', display: 'flex' } },
            path: 'Accueil',
            link: '/',
        },
        { path: sectionLabel, link: listPath },
        '',
    ];

    const visibleSubtitle = subtitleElements.filter(
        ({ label }) => label !== undefined && label !== null && label !== '',
    );

    const hasReference = reference !== undefined && reference !== null && reference !== '';

    return (
        <>
            <Toolbar
                path={breadcrumbPath}
                previousPath={listPath}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        flexShrink: 0,
                        borderRadius: 2,
                        fontSize: 28,
                        marginRight: '20px',
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    }}
                >
                    <FaOptionIcon {...icon.props} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ marginBottom: visibleSubtitle.length ? '8px' : 0 }}
                    >
                        <Typography sx={{ fontSize: '24px', fontWeight: 500, marginRight: '10px' }}>
                            {title}
                        </Typography>
                        {hasReference && (
                            <Tooltip
                                title="Identifiant base de donnée"
                                placement="top"
                            >
                                <Typography sx={{ opacity: 0.8, fontWeight: 400, cursor: 'help' }}>
                                    #{reference}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>

                    {visibleSubtitle.length > 0 && (
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            sx={{ gap: '20px' }}
                        >
                            {visibleSubtitle.map(
                                ({ icon: elementIcon, label, link, linkTooltip }) => (
                                    <Box
                                        key={`${String(label)}`}
                                        sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                opacity: 0.6,
                                            }}
                                        >
                                            <TextIcon
                                                icon={{ props: { icon: elementIcon } }}
                                                gap="6px"
                                            >
                                                <Typography sx={{ fontSize: '0.875em' }}>
                                                    {label}
                                                </Typography>
                                            </TextIcon>
                                        </Box>
                                        {link && (
                                            <Box
                                                component={Link}
                                                to={link}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    fontSize: '0.7em',
                                                }}
                                            >
                                                <FaOptionIcon
                                                    icon={LINK_ICON}
                                                    tooltip={linkTooltip ?? 'Ouvrir'}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                ),
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default FicheHeader;
